// Client-side AES-GCM encryption for Private (end-to-end) rooms.
// The passphrase never leaves the browser; the server only ever stores ciphertext.
const enc = new TextEncoder();
const dec = new TextDecoder();

const keyCache = new Map();

async function deriveKey(roomId, passphrase) {
  const base = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode(`chatmilu:${roomId}`), iterations: 150000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export function savedPassphrase(roomId) {
  return localStorage.getItem(`cml_key_${roomId}`) || "";
}

export async function unlockRoom(roomId, passphrase) {
  const key = await deriveKey(roomId, passphrase);
  keyCache.set(roomId, key);
  localStorage.setItem(`cml_key_${roomId}`, passphrase);
  return key;
}

async function getKey(roomId) {
  if (keyCache.has(roomId)) return keyCache.get(roomId);
  const pass = savedPassphrase(roomId);
  if (!pass) return null;
  return unlockRoom(roomId, pass);
}

export async function encryptText(roomId, text) {
  const key = await getKey(roomId);
  if (!key) throw new Error("Room is locked");
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buf = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(text));
  const bytes = new Uint8Array(buf);
  const b64 = (arr) => btoa(String.fromCharCode(...arr));
  return `cml1:${b64(iv)}:${b64(bytes)}`;
}

export async function decryptText(roomId, payload) {
  if (typeof payload !== "string" || !payload.startsWith("cml1:")) return payload;
  const key = await getKey(roomId);
  if (!key) return "🔒 Locked — enter the room passphrase to read";
  try {
    const [, ivB64, dataB64] = payload.split(":");
    const toBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const buf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toBytes(ivB64) },
      key,
      toBytes(dataB64)
    );
    return dec.decode(buf);
  } catch {
    return "🔒 Cannot decrypt — wrong passphrase";
  }
}
